import type React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PersonIcon from "@mui/icons-material/Person";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Button } from "flowbite-react";

// Define the Query interface
export interface Query {
  _id: string;
  questionNumber: string;
  questionText: string;
  status: string;
  submittedDate: string;
  isDelete: boolean;
  answer?: string;
  answeredBy?: string;
}

export interface QueryDetailsModalProps {
  open: boolean;
  onClose: () => void;
  query: Query | null;
}

// Define type for status colors
type StatusColor = "success" | "warning" | "error" | "info";

const QueryDetailsModal: React.FC<QueryDetailsModalProps> = ({
  open,
  onClose,
  query,
}) => {
  if (!query) return null;

  const getStatusColor = (status: string): StatusColor => {
    switch (status.toLowerCase()) {
      case "completed":
      case "responded":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "info";
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: "#1E3359",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Query Details</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Status Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Chip
              label={query.status}
              color={getStatusColor(query.status)}
              sx={{ mt: 1 }}
            />
          </Grid>

          {/* Query Text Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Query Text
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {query.questionText}
            </Typography>
          </Grid>

          {/* Query Details Grid */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" />
                Query ID
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {query.questionNumber}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon fontSize="small" />
                Submission Date
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {formatDate(query.submittedDate)}
            </Typography>
          </Grid>

          {/* Answer Section - Only show when status is responded */}
          {query.status.toLowerCase() === "responded" && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <QuestionAnswerIcon fontSize="small" />
                    Answer
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: "rgba(30, 51, 89, 0.1)",
                    borderRadius: 1,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {query.answer}
                </Typography>
              </Grid>

              {query.answeredBy && (
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountCircleIcon fontSize="small" />
                      Answered By
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {query.answeredBy}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {/* Status Message */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                color:
                  query.status.toLowerCase() === "responded"
                    ? "success.main"
                    : "warning.main",
              }}
            >
              {query.status.toLowerCase() === "responded" ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <ErrorIcon fontSize="small" />
              )}
              <Typography variant="body2">
                {query.status.toLowerCase() === "responded"
                  ? "This query has been answered"
                  : "This query is still pending"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} className="bg-[#1E3359]">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QueryDetailsModal;
